/* eslint-disable no-console */
import { getCookie } from 'api/utils';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { io } from 'socket.io-client';
import { v4 as uuidV4 } from 'uuid';

import PropTypes from 'prop-types';
import { webSocketActions } from 'store/slices/webSocketSlice';
import { handleAppLockedLineItems } from 'store/thunks/lockedLineItemsThunks';

const webSocketContext = createContext();
const webSocketUrl = process.env.REACT_APP_PRX_WEBSOCKET_URL;
const isMockMode =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCKS === 'true';

const createMockSocket = () => {
  const handlers = new Map();
  const socket = {
    connected: true,
    on: (event, callback) => {
      handlers.set(event, [...(handlers.get(event) ?? []), callback]);
    },
    off: (event, callback) => {
      handlers.set(
        event,
        (handlers.get(event) ?? []).filter((registered) => registered !== callback)
      );
    },
    emit: () => {},
    removeAllListeners: () => handlers.clear(),
    disconnect: () => {},
    receive: (event, data) => {
      (handlers.get(event) ?? []).forEach((callback) => callback(data));
    },
  };

  window.prxMockSocket = socket;

  return socket;
};

export const WebSocketProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [connectionState, setConnectionState] = useState('DISCONNECTED');
  const [webSocket, setWebSocket] = useState(null);

  const sendMessage = useCallback(
    (event, data) => {
      if (webSocket?.connected) {
        try {
          webSocket.emit(event, data);
        } catch (error) {
          console.error('Error sending message:', error);
        }
      } else {
        console.warn('Socket.IO is not connected. Cannot send message.');
      }
    },
    [webSocket]
  );

  const connectSocket = useCallback(() => {
    if (isMockMode) {
      setWebSocket(createMockSocket());
      setIsConnected(true);
      setConnectionState('CONNECTED');
      return;
    }

    if (!webSocketUrl) {
      setConnectionError('No URL provided');
      return;
    }

    setConnectionState('CONNECTING');

    const token = getCookie('pomAccessToken');

    // Default Socket.IO options with authentication
    const defaultOptions = {
      transports: ['websocket', 'polling'],
      upgrade: true,
      rememberUpgrade: true,
      // Send authentication data during connection (token only)
      auth: {
        token,
      },
      // Also send as query params for additional compatibility
      query: {
        token,
      },
    };

    try {
      const socket = io(webSocketUrl, defaultOptions);
      setWebSocket(socket);

      // Set up global listener for app_locked_line_items immediately when socket is created
      // This ensures we catch the event even if server sends it right after connection
      const handleAppLockedLineItemsEvent = (data) => {
        const timestamp = new Date().toISOString();
        const message = { event: 'app_locked_line_items', data, timestamp };
        dispatch(webSocketActions.addWebSocketMessage(message));
        dispatch(handleAppLockedLineItems({ data }));
      };

      socket.on('app_locked_line_items', handleAppLockedLineItemsEvent);

      socket.on('connect', () => {
        setIsConnected(true);
        setConnectionError(null);
        setConnectionState('CONNECTED');
      });

      socket.on('connect_error', (error) => {
        setConnectionError(`Connection error: ${error.message}`);
        setConnectionState('ERROR');
      });

      socket.on('disconnect', (reason) => {
        setIsConnected(false);
        setConnectionState('DISCONNECTED');

        if (reason === 'io server disconnect') {
          // Server disconnected the socket, reconnection is needed
          setConnectionError('Server disconnected the connection');
        } else if (reason === 'transport close' || reason === 'transport error') {
          setConnectionError(`Connection issue: ${reason}`);
        }
      });

      socket.on('error', (error) => {
        setConnectionError(`Socket error: ${error.message || error}`);
        setConnectionState('ERROR');
      });
    } catch (error) {
      setConnectionError(error.message);
      setConnectionState('ERROR');
    }
  }, [dispatch]);

  const closeSnackbar = useCallback(() => {
    dispatch(webSocketActions.closeSnackbar());
  }, [dispatch]);

  const reconnect = useCallback(() => {
    webSocket?.disconnect();
    connectSocket();
  }, [connectSocket, webSocket]);

  const on = useCallback((event, callback) => webSocket?.on(event, callback), [webSocket]);
  const off = useCallback((event, callback) => webSocket?.off(event, callback), [webSocket]);

  useEffect(() => {
    if (!webSocket) {
      connectSocket();
    }

    // cleanup
    return () => {
      if (webSocket) {
        webSocket.removeAllListeners();
        webSocket.disconnect();
        setWebSocket(null);
      }
    };
  }, [connectSocket, webSocket]);

  useEffect(() => {
    if (sessionStorage.getItem('socketSessionId') === null) {
      sessionStorage.setItem('socketSessionId', uuidV4());
    }
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isConnected,
      sendMessage,
      connectionError,
      connectionState,
      reconnect,
      on,
      off,
      socket: webSocket,
      closeSnackbar,
    }),
    [
      isConnected,
      sendMessage,
      connectionError,
      connectionState,
      reconnect,
      on,
      off,
      webSocket,
      closeSnackbar,
    ]
  );

  return <webSocketContext.Provider value={memoizedValue}>{children}</webSocketContext.Provider>;
};

WebSocketProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

const useWebSocket = () => useContext(webSocketContext);

export default useWebSocket;
